import { useRef, useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useFeatureFlagWithLoading } from '@/contexts/FeatureFlagContext';
import { useUnreadNotificationCount } from '@/hooks/useNotifications';
import { BellIcon } from '@/components/icons/BellIcon';
import { NotificationDropdown } from './NotificationDropdown';

export function NotificationBell() {
  const { isSignedIn } = useAuth();
  const { enabled: showNotifications } = useFeatureFlagWithLoading('show_notifications');
  const { data } = useUnreadNotificationCount();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const count = data?.count ?? 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  if (!isSignedIn || !showNotifications) return null;

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="relative p-1.5 text-warm-gray hover:text-ink transition-colors rounded focus:outline-none focus:ring-2 focus:ring-ink"
        aria-label="Notifications"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <BellIcon className="w-5 h-5" />
        {/* Unread badge */}
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {isOpen && <NotificationDropdown onClose={() => setIsOpen(false)} />}
    </div>
  );
}
